import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  FiRefreshCw, FiGrid, FiHelpCircle, FiList, FiPlay, FiTrash2, FiX, FiAlertTriangle, FiDollarSign, FiFolder,
} from "react-icons/fi";
import { GoOrganization } from "react-icons/go";
import api from "../../services/api";
import { LiveDot, Pill, Group, Empty, Section } from "../activity-logs/styles";
import { SegmentedControl } from "../activity-logs/components/primitives";
import { IOS } from "../activity-logs/labels";
import { ActionButton, CreditsPage } from "./styles";
import {
  Capsule, PeriodPicker, PrimaryCapsule, RoundButton, SelectField, TintedDanger, Toolbar, ToolbarDivider,
} from "./components/ControlBar";
import { EMPTY_KPIS, LIST_LIMIT, buildDashboard, formatMonth, mapCredito } from "./dashboardData";
import { useMonthlySeries, useSuppliers } from "./useCreditosData";
import Overview from "./components/Overview";
import CreditList from "./components/CreditList";
import SupplierDashboard from "./components/SupplierDashboard";
import CreditoDetailScreen from "./CreditoDetailScreen";
import SupplierDetailScreen from "./SupplierDetailScreen";
import AnaliseCreditsModal from "./modals/AnaliseCreditsModal";
import { ScrolledTopBar, UpdatedAgo } from "./components/parts";
import useGuidedTour from "../../components/guided-tour/useGuidedTour";
import TourOverlay from "../../components/guided-tour/TourOverlay";
import TourLauncher, { TourOffer } from "../../components/guided-tour/TourLauncher";
import creditosTour from "./creditosTour";

const currentProject = () => localStorage.getItem("vidal_selected_project");

export default function Creditos() {
  const [project, setProject] = useState(currentProject);
  const [tab, setTab] = useState("overview");
  const [updatedAt, setUpdatedAt] = useState(null);

  // Filtros aplicados no backend
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [empresa, setEmpresa] = useState("");

  // Filtros aplicados na lista de créditos
  const [tax, setTax] = useState("all");
  const [category, setCategory] = useState("");

  const [creditos, setCreditos] = useState([]);
  const [kpis, setKpis] = useState(EMPTY_KPIS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [analiseModalOpen, setAnaliseModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedCredito, setSelectedCredito] = useState(null);

  const requestId = useRef(0);
  const projectRef = useRef(project);
  const inDetail = !!(selectedCredito || selectedSupplier);

  // A troca de projeto não dispara "storage" na mesma aba, daí o polling
  useEffect(() => {
    const sync = () => {
      const next = currentProject();
      if (next === projectRef.current) return;
      projectRef.current = next;
      // Nada do projeto anterior pode continuar na tela
      setProject(next);
      setCreditos([]);
      setKpis(EMPTY_KPIS);
      setEmpresa("");
      setSelectedSupplier(null);
      setSelectedCredito(null);
    };
    window.addEventListener("storage", sync);
    const interval = setInterval(sync, 1000);
    return () => {
      window.removeEventListener("storage", sync);
      clearInterval(interval);
    };
  }, []);

  const loadCreditos = useCallback(async () => {
    // Só a resposta da última requisição vale, mesmo que outra chegue depois
    const id = ++requestId.current;
    if (!project) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const params = { projeto: project, limit: LIST_LIMIT };
      if (dataInicio) params.dataInicio = dataInicio;
      if (dataFim) params.dataFim = dataFim;
      if (empresa) params.empresa = empresa;

      const { data } = await api.get("/creditos/lista", { params });
      if (id !== requestId.current) return;
      if (!data.success) throw new Error("Resposta sem sucesso");

      setCreditos((data.creditos || []).map(mapCredito));
      setKpis(data.kpis || EMPTY_KPIS);
      setUpdatedAt(new Date().toISOString());
    } catch (err) {
      if (id !== requestId.current) return;
      console.error("Erro ao carregar créditos:", err);
      // Dados antigos não correspondem mais aos filtros pedidos
      setCreditos([]);
      setKpis(EMPTY_KPIS);
      setError(true);
      toast.error("Erro ao carregar créditos");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [project, dataInicio, dataFim, empresa]);

  useEffect(() => { loadCreditos(); }, [loadCreditos]);

  // Recarrega a tela e descarta o cache da série e dos fornecedores
  const refresh = useCallback(() => {
    setReloadKey((k) => k + 1);
    loadCreditos();
  }, [loadCreditos]);

  const series = useMonthlySeries({
    project, empresa, dataInicio, dataFim, total: kpis.totalCreditos || 0, ready: !loading && !error, reloadKey,
  });
  const suppliers = useSuppliers({ project, creditos, empresa, ready: !loading && !error, reloadKey });

  const handleReset = async () => {
    if (!project) return;
    const confirmed = window.confirm(
      "Tem certeza que deseja apagar todos os créditos deste projeto? Esta ação não pode ser desfeita."
    );
    if (!confirmed) return;
    setResetting(true);
    try {
      const response = await api.delete("/creditos/reset", { params: { projeto: project } });
      if (response.data.success) {
        setCreditos([]);
        setKpis(EMPTY_KPIS);
        setSelectedSupplier(null);
      }
    } catch (err) {
      console.error("Erro ao resetar créditos:", err);
      toast.error("Erro ao resetar créditos");
    } finally {
      setResetting(false);
    }
  };

  const dashboard = useMemo(() => buildDashboard(creditos), [creditos]);

  // Tutorial guiado: dados reais e ações da tela
  const topSupplier = useMemo(
    () => [...suppliers.suppliers].sort((a, b) => (b.total || 0) - (a.total || 0))[0] || null,
    [suppliers.suppliers],
  );
  const periodFiltered = !!(dataInicio || dataFim);
  const hasFilters = periodFiltered || !!empresa;
  const clearFilters = useCallback(() => { setDataInicio(""); setDataFim(""); setEmpresa(""); }, []);

  // O que a tela mostra agora. Cada situação tem o seu tutorial, ou o guia explica por que não tem
  let screen = "empty";
  if (!project) screen = "no-project";
  else if (error) screen = "error";
  else if (loading && creditos.length === 0) screen = "loading";
  else if (creditos.length > 0) screen = "data";
  else if (hasFilters) screen = "filtered";

  const tourContext = useMemo(() => ({
    ready: !loading && !inDetail && !analiseModalOpen,
    screen,
    hasData: creditos.length > 0,
    hasFilters,
    clearFilters,
    analiseOpen: analiseModalOpen,
    setAnaliseOpen: setAnaliseModalOpen,
    suppliersLoading: suppliers.loading,
    tab,
    setTab,
    kpis,
    itemCount: dashboard.itemCount,
    categories: dashboard.categories,
    months: series.months,
    formatMonth,
    topSupplier,
    supplierCount: suppliers.suppliers.length,
  }), [loading, inDetail, analiseModalOpen, screen, hasFilters, clearFilters, suppliers.loading, creditos.length, tab, kpis, dashboard, series.months, topSupplier, suppliers.suppliers.length]);
  const tour = useGuidedTour(creditosTour, tourContext);

  const goCredits = ({ tax: nextTax = "all", category: nextCategory = "" } = {}) => {
    setTax(nextTax);
    setCategory(nextCategory);
    setTab("credits");
  };

  const selectMonth = (key) => { setDataInicio(key); setDataFim(key); };

  // Telas de detalhe já existentes, que consultam a API por conta própria
  if (selectedSupplier) {
    return (
      <SupplierDetailScreen
        supplier={selectedSupplier}
        allSuppliers={suppliers.suppliers}
        periodFiltered={periodFiltered}
        onBack={() => setSelectedSupplier(null)}
      />
    );
  }
  if (selectedCredito) {
    return (
      <CreditoDetailScreen
        credito={selectedCredito}
        series={series}
        periodFiltered={periodFiltered}
        onBack={() => setSelectedCredito(null)}
      />
    );
  }

  const initialLoading = loading && creditos.length === 0;
  const isEmpty = !loading && !error && creditos.length === 0;
  const empresas = kpis.empresas || [];
  const { months } = series;
  const periodLabel = months.length > 0
    ? `${formatMonth(months[0].key)} — ${formatMonth(months[months.length - 1].key)}`
    : "";

  let content;
  if (!project) {
    content = (
      <Section $first>
        <Group>
          <Empty>
            <div className="e-icon"><FiFolder /></div>
            <div className="e-title">Nenhum projeto selecionado</div>
            <div className="e-sub">Selecione um projeto para ver os créditos.</div>
          </Empty>
        </Group>
      </Section>
    );
  } else if (error) {
    content = (
      <Section $first>
        <Group>
          <Empty>
            <div className="e-icon"><FiAlertTriangle /></div>
            <div className="e-title">Não foi possível carregar os créditos</div>
            <div className="e-sub" style={{ marginBottom: 14 }}>Verifique a conexão e tente de novo.</div>
            <ActionButton onClick={refresh} style={{ margin: "0 auto" }}><FiRefreshCw /> Tentar novamente</ActionButton>
          </Empty>
        </Group>
      </Section>
    );
  } else if (isEmpty) {
    content = (
      <Section $first>
        <Group data-tour="empty">
          <Empty>
            <div className="e-icon"><FiDollarSign /></div>
            <div className="e-title">{hasFilters ? "Nenhum crédito para estes filtros" : "Nenhum crédito levantado ainda"}</div>
            <div className="e-sub" style={{ marginBottom: 14 }}>
              {hasFilters ? "Mude o período ou a empresa para ver outros resultados." : "Execute uma análise para identificar oportunidades."}
            </div>
            {hasFilters
              ? <ActionButton data-tour="empty-clear" onClick={clearFilters} style={{ margin: "0 auto" }}><FiX /> Limpar filtros</ActionButton>
              : (
                <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                  <ActionButton data-tour="empty-start" onClick={() => setAnaliseModalOpen(true)}><FiPlay /> Iniciar análise</ActionButton>
                  <Capsule type="button" onClick={() => tour.start("first-analysis")}><FiHelpCircle /> Ver como funciona</Capsule>
                </div>
              )}
          </Empty>
        </Group>
      </Section>
    );
  } else if (tab === "overview" || initialLoading) {
    content = (
      <Overview
        kpis={kpis}
        dashboard={dashboard}
        series={series}
        suppliers={suppliers}
        periodFiltered={periodFiltered}
        loading={initialLoading}
        onGoCredits={goCredits}
        onGoSuppliers={() => setTab("suppliers")}
        onOpenCredit={setSelectedCredito}
        onOpenSupplier={setSelectedSupplier}
        onSelectMonth={selectMonth}
      />
    );
  } else if (tab === "suppliers") {
    content = (
      <SupplierDashboard
        suppliers={suppliers.suppliers}
        loading={suppliers.loading}
        failed={suppliers.failed}
        periodFiltered={periodFiltered}
        onOpen={setSelectedSupplier}
      />
    );
  } else {
    content = (
      <CreditList
        creditos={creditos}
        total={kpis.totalCreditos || 0}
        tax={tax}
        setTax={setTax}
        category={category}
        setCategory={setCategory}
        onOpen={setSelectedCredito}
      />
    );
  }

  return (
    <>
      <CreditsPage>
        <ScrolledTopBar deps={[inDetail]}>
          <div className="title-row">
            <div>
              <div className="eyebrow">Vidal · Levantamento tributário</div>
              <h1>Créditos</h1>
              <div className="meta">
                {error
                  ? <Pill $color={IOS.red}>Falha ao carregar</Pill>
                  : <LiveDot>Dados da API</LiveDot>}
                {periodLabel && <span className="update-tag">{periodLabel}</span>}
                <UpdatedAgo at={updatedAt} />
              </div>
            </div>
          </div>

          <div className="nav-row">
            <SegmentedControl
              value={tab}
              onChange={setTab}
              options={[
                { key: "overview", icon: <FiGrid />, label: "Visão geral" },
                { key: "suppliers", icon: <GoOrganization />, label: "Fornecedores", count: suppliers.loading ? undefined : suppliers.suppliers.length },
                { key: "credits", icon: <FiList />, label: "Tipos de crédito", count: initialLoading ? undefined : creditos.length },
              ]}
            />
            {/* Filtros e ações em uma linha, à direita das abas */}
            <Toolbar>
              <span data-tour="period" style={{ display: "inline-flex" }}><PeriodPicker
                from={dataInicio}
                to={dataFim}
                available={months.map((m) => m.key)}
                onChange={(from, to) => { setDataInicio(from); setDataFim(to); }}
              /></span>
              {empresas.length > 0 && (
                <SelectField icon={<GoOrganization />} label="Empresa" value={empresa} onChange={setEmpresa} active={!!empresa}>
                  <option value="">Todas as empresas</option>
                  {empresas.map((emp) => (
                    <option key={emp.cnpj} value={emp.cnpj}>{emp.nome} ({emp.cnpj})</option>
                  ))}
                </SelectField>
              )}
              {hasFilters && (
                <RoundButton type="button" onClick={clearFilters} title="Limpar filtros" aria-label="Limpar filtros"><FiX /></RoundButton>
              )}
              <ToolbarDivider />
              <RoundButton type="button" onClick={refresh} title="Atualizar" aria-label="Atualizar">
                <FiRefreshCw className={loading || series.loading || suppliers.loading ? "spin" : ""} />
              </RoundButton>
              {creditos.length > 0 && (
                <TintedDanger type="button" onClick={handleReset} disabled={resetting}>
                  <FiTrash2 /> {resetting ? "Resetando…" : "Resetar"}
                </TintedDanger>
              )}
              <TourLauncher tour={tour} />
              <PrimaryCapsule data-tour="analise" type="button" disabled={!project} onClick={() => setAnaliseModalOpen(true)}>
                <FiPlay /> Iniciar análise
              </PrimaryCapsule>
            </Toolbar>
          </div>
        </ScrolledTopBar>

        {content}
      </CreditsPage>

      <TourOffer tour={tour} />
      <TourOverlay tour={tour} />

      <AnaliseCreditsModal
        isOpen={analiseModalOpen}
        onClose={() => setAnaliseModalOpen(false)}
        onComplete={refresh}
        preview={!!tour.active}
      />
    </>
  );
}
